import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { lessonActions } from '../actions/lesson.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class LessonEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getLesson$ = createEffect(() => {
        return this.actions.pipe(
            ofType(lessonActions.getLesson),
            mergeMap(({ id }) => {
                return this.httpService.getLesson(id).pipe(
                    finalizeLoader('lesson-get'),
                    map((lesson) => lessonActions.getLessonSuccess({ payload: lesson })),
                    catchError((response: HttpErrorResponse) => of(lessonActions.getLessonError({ code: response.status }))),
                );
            }),
        );
    });

    createLesson$ = createEffect(() => {
        return this.actions.pipe(
            ofType(lessonActions.createLesson),
            mergeMap(({ value }) => {
                return this.httpService.createLesson(value).pipe(
                    finalizeLoader('lesson-create'),
                    map((lesson) => lessonActions.createLessonSuccess({ payload: lesson })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.lessons.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.lessons.createError');
                        return of(lessonActions.createLessonError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateLesson$ = createEffect(() => {
        return this.actions.pipe(
            ofType(lessonActions.updateLesson),
            mergeMap(({ id, value }) => {
                return this.httpService.updateLesson(id, value).pipe(
                    finalizeLoader('lesson-update'),
                    map((lesson) => lessonActions.updateLessonSuccess({ payload: lesson })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.lessons.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.lessons.updateError');
                        return of(lessonActions.updateLessonError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteLesson$ = createEffect(() => {
        return this.actions.pipe(
            ofType(lessonActions.deleteLesson),
            mergeMap(({ id }) => {
                return this.httpService.deleteLesson(id).pipe(
                    finalizeLoader('lesson-delete'),
                    map(() => lessonActions.deleteLessonSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.lessons.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.lessons.deleteError');
                        return of(lessonActions.deleteLessonError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
